export type SeverityLevel = 'critical' | 'warning' | 'pass';
export type ImpactLevel = 'high' | 'medium' | 'low';
export type EffortLevel = 'low' | 'medium' | 'high';

export interface CheckResult {
  id: string;
  score: number; // 0–10
  severity: SeverityLevel;
  finding: string;
}

export interface Recommendation {
  id: string;
  checkId: string;
  title: string;
  summary: string;
  impact: ImpactLevel;
  effort: EffortLevel;
  whyItMatters: string;
  steps: string[];
  benchmark?: string;
}

export interface AuditResult {
  url: string;
  overallScore: number;
  checks: CheckResult[];
  recommendations: Recommendation[];
  auditedAt: string;
  metrics?: {
    lcp: string | null;
    fcp: string | null;
    cls: string | null;
    tbt: string | null;
    performanceScore: number;
  };
}

const ALL_RECOMMENDATIONS: Record<string, Recommendation> = {
  'load-time-critical': {
    id: 'rec-load-time-critical',
    checkId: 'load-time',
    title: 'Reduce page load time below 2.5 seconds',
    summary: 'LCP above 4s loses 80% of mobile visitors before they see your value prop',
    impact: 'high',
    effort: 'medium',
    whyItMatters: "Google's research shows a 1-second delay in mobile load times can reduce conversion rates by up to 20%. High LCP means a significant portion of your traffic bounces before seeing your CTA.",
    steps: [
      'Run PageSpeed Insights (pagespeed.web.dev) — identify your largest contentful paint element',
      'Compress and convert hero images to WebP format (typical 60-80% size reduction)',
      'Move to a CDN — Cloudflare Free tier is sufficient for most landing pages',
      'Defer non-critical JavaScript and inline critical CSS above the fold',
      'Remove unused third-party scripts — check for analytics/chat widgets loading synchronously',
    ],
    benchmark: 'Industry benchmark: Top-converting SaaS landing pages average 1.8s LCP. Improving to under 2.5s typically yields 15–25% more sessions that reach the CTA.',
  },
  'load-time-warning': {
    id: 'rec-load-time-warning',
    checkId: 'load-time',
    title: 'Optimize load time to under 2s',
    summary: "You're at the borderline — mobile visitors on 4G connections will experience longer load times",
    impact: 'medium',
    effort: 'medium',
    whyItMatters: 'While borderline LCP is acceptable on desktop, mobile users on average 4G connections experience 40-60% longer load times. Optimizing to under 2s protects your mobile conversion rate.',
    steps: [
      'Audit your image sizes — compress any images larger than 200KB',
      'Enable lazy loading on images below the fold',
      'Check for render-blocking resources in your HTML head',
      'Consider switching to a faster hosting provider or enabling caching headers',
    ],
    benchmark: 'Reducing LCP to 1.8s typically improves mobile conversion rate by 8–12%.',
  },
  'cta-above-fold-critical': {
    id: 'rec-cta-above-fold',
    checkId: 'cta-above-fold',
    title: 'Move primary CTA above the fold',
    summary: "Visitors who don't see a CTA immediately have a 35% lower conversion rate",
    impact: 'high',
    effort: 'low',
    whyItMatters: 'The fold is the most valuable real estate on your landing page. Visitors who do not see a clear action to take within the first 3 seconds are significantly more likely to leave.',
    steps: [
      'Place your primary CTA button in the hero section — ideally within the first 600px of page height',
      'Use a contrasting button color that stands out from your background',
      'Write action-oriented button copy: "Start free trial", "Get my free audit", not just "Sign up"',
      'Add a secondary micro-CTA (e.g. "Watch 2-min demo") for visitors not ready to convert',
      'Test button placement with a 5-second test — can a stranger identify the next action instantly?',
    ],
    benchmark: 'Moving a CTA from below-fold to above-fold has been shown to increase click-through rates by 25–40% in A/B tests across SaaS landing pages.',
  },
  'cta-above-fold-warning': {
    id: 'rec-cta-above-fold-warning',
    checkId: 'cta-above-fold',
    title: 'Improve CTA visibility on slow connections',
    summary: 'Slow FCP means your CTA may not render before visitors decide to leave',
    impact: 'medium',
    effort: 'medium',
    whyItMatters: 'Even if your CTA is technically above the fold, a slow First Contentful Paint means visitors see a blank or partially loaded page before it appears.',
    steps: [
      'Inline critical CSS for your hero section to ensure it renders immediately',
      'Preload your hero font and CTA button styles',
      'Use a skeleton loader so visitors see structure while content loads',
    ],
    benchmark: 'Improving FCP to under 1.8s typically reduces bounce rate by 10–15%.',
  },
  'headline-clarity-warning': {
    id: 'rec-headline-clarity',
    checkId: 'headline-clarity',
    title: 'Rewrite headline to communicate a specific outcome',
    summary: 'Generic headlines fail the 5-second clarity test',
    impact: 'high',
    effort: 'low',
    whyItMatters: 'Your headline is the first thing every visitor reads. If it does not immediately answer "what does this do for me?", visitors leave before reading anything else.',
    steps: [
      'Use the formula: [Specific outcome] for [specific audience] [without/in X time/without X pain]',
      'Include a quantified benefit where possible — "Cut your onboarding time by 60%" beats "Faster onboarding"',
      'Run a 5-second test (usabilityhub.com) — ask 5 strangers what they think your product does',
      'Write 10 headline variants before choosing — your first draft is rarely the best',
    ],
    benchmark: 'Clarity-focused headline rewrites average a 10–30% improvement in time-on-page and scroll depth.',
  },
  'social-proof-critical': {
    id: 'rec-social-proof',
    checkId: 'social-proof',
    title: 'Add social proof signals to your hero section',
    summary: '92% of buyers read reviews before purchasing — zero social proof is a major trust barrier',
    impact: 'high',
    effort: 'medium',
    whyItMatters: 'Visitors who are unfamiliar with your brand need third-party validation to trust you. Without any testimonials, logos, or user counts, you are asking them to convert on faith alone.',
    steps: [
      'Add a customer logo bar (5–8 recognizable company logos) directly below your hero CTA',
      'Include at least 3 specific testimonials with full name, role, company, and photo',
      'Add a user count stat if you have one: "Trusted by 2,400+ founders"',
      'Consider a G2/Capterra rating badge if you have reviews there',
      'If you have no customers yet, use beta tester quotes or advisor endorsements',
    ],
    benchmark: 'Adding a logo bar and 3 testimonials to a landing page without prior social proof has been shown to increase conversion rates by 15–30%.',
  },
  'social-proof-warning': {
    id: 'rec-social-proof-warning',
    checkId: 'social-proof',
    title: 'Add structured data markup for reviews and ratings',
    summary: 'Structured data helps search engines and visitors find your social proof signals',
    impact: 'medium',
    effort: 'low',
    whyItMatters: 'Adding schema.org markup for reviews and ratings makes your social proof visible in search results and signals credibility to both visitors and search engines.',
    steps: [
      'Add Review or AggregateRating schema.org markup to your testimonials section',
      'Use Google Rich Results Test to verify your markup',
      'Ensure testimonials include specific outcomes and company names',
    ],
    benchmark: 'Pages with review structured data see 15–20% higher click-through rates from search results.',
  },
  'pricing-clarity-critical': {
    id: 'rec-pricing-clarity',
    checkId: 'pricing-clarity',
    title: 'Make pricing visible — stop hiding it',
    summary: "Hiding pricing filters out high-intent buyers who won't fill out a form just to see a number",
    impact: 'high',
    effort: 'medium',
    whyItMatters: '"Contact sales for pricing" is one of the highest-friction patterns in B2B SaaS. Buyers who cannot self-qualify on price will leave and evaluate competitors who show pricing.',
    steps: [
      'Publish at least a pricing range or "starting from $X/month" to anchor expectations',
      'Create a self-serve tier (even a free trial or freemium) so visitors can start without talking to sales',
      'If enterprise pricing is custom, show SMB/startup tiers publicly and gate only enterprise',
      'Add a pricing FAQ that addresses common objections: "Can I cancel anytime?", "Is there a free trial?"',
      'Link to your pricing page prominently in your hero section and navigation',
    ],
    benchmark: 'Openness about pricing is correlated with 20–40% higher trial conversion rates in self-serve SaaS.',
  },
  'pricing-clarity-warning': {
    id: 'rec-pricing-clarity-warning',
    checkId: 'pricing-clarity',
    title: 'Improve pricing page link clarity',
    summary: 'Unclear link text makes it hard for visitors to find your pricing',
    impact: 'medium',
    effort: 'low',
    whyItMatters: 'Visitors who cannot easily find pricing information are more likely to leave than to search for it. Clear, descriptive navigation links reduce this friction.',
    steps: [
      'Use "Pricing" as the exact navigation label — not "Plans" or "Cost"',
      'Add a pricing link in your hero section CTA area',
      'Show the annual savings prominently: "Save 20% — 2 months free"',
    ],
    benchmark: 'Clear pricing navigation increases pricing page visits by 20–30%.',
  },
  'mobile-responsive-critical': {
    id: 'rec-mobile-responsive',
    checkId: 'mobile-responsive',
    title: 'Add viewport meta tag for mobile responsiveness',
    summary: 'Missing viewport meta tag means your page is not mobile-optimized',
    impact: 'high',
    effort: 'low',
    whyItMatters: 'Over 60% of web traffic is mobile. Without a viewport meta tag, your page renders at desktop width on mobile devices, making text tiny and CTAs impossible to tap.',
    steps: [
      'Add <meta name="viewport" content="width=device-width, initial-scale=1"> to your HTML head',
      'Test your page on Google Mobile-Friendly Test',
      'Ensure all buttons and links have at least 44x44px tap targets',
      'Use responsive CSS units (%, vw, rem) instead of fixed pixel widths',
    ],
    benchmark: 'Mobile-optimized pages convert at 2-3x the rate of non-responsive pages for mobile visitors.',
  },
  'form-friction-warning': {
    id: 'rec-form-friction',
    checkId: 'form-friction',
    title: 'Improve touch target sizes to reduce form friction',
    summary: 'Small tap targets on mobile increase form abandonment',
    impact: 'medium',
    effort: 'low',
    whyItMatters: 'Mobile users who struggle to tap small buttons or form fields are significantly more likely to abandon the form. Google recommends a minimum 48x48px tap target size.',
    steps: [
      'Increase button and input field height to at least 44px on mobile',
      'Add adequate spacing between interactive elements (minimum 8px)',
      'Use larger font sizes for form labels (minimum 16px to prevent iOS zoom)',
      'Consider using social auth (Google/GitHub) to eliminate the password field entirely',
    ],
    benchmark: 'Fixing tap target sizes reduces mobile form abandonment by 15–25%.',
  },
  'trust-signals-critical': {
    id: 'rec-trust-signals-critical',
    checkId: 'trust-signals',
    title: 'Enable HTTPS immediately',
    summary: 'HTTP sites are flagged as "Not Secure" by browsers — a critical conversion killer',
    impact: 'high',
    effort: 'low',
    whyItMatters: 'Modern browsers display a "Not Secure" warning for HTTP sites. This warning appears directly in the address bar and immediately destroys visitor trust before they even read your headline.',
    steps: [
      'Enable SSL/TLS certificate on your hosting provider (free with Let\'s Encrypt)',
      'Force HTTPS redirects for all HTTP traffic',
      'Update all internal links and assets to use HTTPS',
      'Verify with SSL Labs (ssllabs.com/ssltest) that your certificate is valid',
    ],
    benchmark: 'Switching from HTTP to HTTPS typically improves conversion rates by 10–20% due to eliminated browser security warnings.',
  },
  'trust-signals-warning': {
    id: 'rec-trust-signals',
    checkId: 'trust-signals',
    title: 'Add privacy policy link and security trust signals',
    summary: 'Missing privacy policy increases visitor anxiety about data handling',
    impact: 'medium',
    effort: 'low',
    whyItMatters: 'GDPR awareness has made visitors increasingly cautious about data privacy. A missing privacy policy link creates subconscious anxiety that reduces conversion.',
    steps: [
      'Add a privacy policy link to your footer (use a generator like termly.io if you do not have one)',
      'Add "No credit card required" text near your primary CTA',
      'Consider adding a brief "Your data is secure" line near signup forms',
      'Link to a security page or add a SOC2/SSL badge if applicable',
    ],
    benchmark: 'Adding a privacy policy link and "No credit card required" near CTAs typically improves conversion by 5–10%.',
  },
  'seo-meta-critical': {
    id: 'rec-seo-meta-critical',
    checkId: 'seo-meta',
    title: 'Fix critical SEO meta tag issues',
    summary: 'Missing title and description tags hurt both search rankings and click-through rates',
    impact: 'medium',
    effort: 'low',
    whyItMatters: 'Meta title and description are the first things potential visitors see in search results. Missing or poor meta tags reduce your search visibility and click-through rates.',
    steps: [
      'Add a unique, descriptive <title> tag (50-60 characters)',
      'Add a compelling meta description (150-160 characters) that includes your value proposition',
      'Create a 1200x630px OG image with your product name and tagline',
      'Add Open Graph tags: og:title, og:description, og:image, og:url',
      'Test your OG tags at opengraph.xyz before publishing',
    ],
    benchmark: 'Pages with compelling meta descriptions see 5-10% higher click-through rates from search results.',
  },
  'seo-meta-warning': {
    id: 'rec-seo-meta',
    checkId: 'seo-meta',
    title: 'Add Open Graph image and complete meta tags',
    summary: 'Missing OG image means your page shows a broken preview when shared on Slack or Twitter',
    impact: 'low',
    effort: 'low',
    whyItMatters: 'When someone shares your URL in Slack, LinkedIn, or Twitter without an OG image, it shows a blank card or your favicon. This dramatically reduces click-through rates from social sharing.',
    steps: [
      'Create a 1200x630px OG image with your product name, tagline, and a visual of your UI',
      'Add <meta property="og:image"> and <meta property="og:title"> tags to your page head',
      'Use Twitter Card tags as well: <meta name="twitter:card" content="summary_large_image">',
      'Test your OG tags at opengraph.xyz before publishing',
    ],
    benchmark: 'Pages with compelling OG images see 2–3x higher click-through rates when shared on social media.',
  },
  'page-structure-critical': {
    id: 'rec-page-structure-critical',
    checkId: 'page-structure',
    title: 'Fix heading hierarchy and landmark structure',
    summary: 'Poor accessibility structure makes your page harder to navigate and understand',
    impact: 'medium',
    effort: 'medium',
    whyItMatters: 'A clear heading hierarchy (H1 → H2 → H3) helps both visitors and search engines understand your page structure. Poor structure increases cognitive load and reduces time on page.',
    steps: [
      'Ensure you have exactly one H1 tag containing your main value proposition',
      'Use H2 tags for major sections (Features, Pricing, Testimonials)',
      'Use H3 tags for subsections within each major section',
      'Add ARIA landmark roles: header, main, nav, footer',
      'Run an accessibility audit with axe DevTools browser extension',
    ],
    benchmark: 'Fixing heading hierarchy typically improves scroll depth by 15–20% as visitors can better navigate your content.',
  },
  'page-structure-warning': {
    id: 'rec-page-structure-warning',
    checkId: 'page-structure',
    title: 'Improve page structure and content hierarchy',
    summary: 'Moderate accessibility issues suggest content hierarchy could be clearer',
    impact: 'medium',
    effort: 'medium',
    whyItMatters: 'Your current page structure has some accessibility issues that also affect how visitors navigate and understand your content.',
    steps: [
      'Move your benefits section to the second section, directly below the hero',
      'Use the structure: Hero → Benefits → How it works → Social proof → Pricing → Final CTA',
      'Lead each section with a benefit headline, not a feature name',
      'Use visuals that show the end result, not the interface',
    ],
    benchmark: 'Restructuring from feature-first to benefit-first page flow typically improves scroll depth by 20–30%.',
  },
};

export function generateRecommendations(checks: CheckResult[]): Recommendation[] {
  const result: Recommendation[] = [];

  checks.forEach(check => {
    const key = `${check.id}-${check.severity}`;
    if (ALL_RECOMMENDATIONS[key]) {
      result.push(ALL_RECOMMENDATIONS[key]);
    }
  });

  const impactOrder: Record<ImpactLevel, number> = { high: 0, medium: 1, low: 2 };
  result.sort((a, b) => impactOrder[a.impact] - impactOrder[b.impact]);

  return result;
}

// Calls the real PageSpeed Insights API via our Next.js API route
export async function runAudit(
  url: string,
  onCheckComplete: (checkId: string) => void
): Promise<AuditResult> {
  const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;

  const response = await fetch(`/api/audit?url=${encodeURIComponent(normalizedUrl)}`);

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(body.error || `Audit failed with status ${response.status}`);
  }

  const data = await response.json();

  // Stream check IDs progressively with small delays for UX
  const CHECK_DELAY_MS = 300;
  for (const check of data.checks) {
    await new Promise<void>(resolve => setTimeout(resolve, CHECK_DELAY_MS));
    onCheckComplete(check.id);
  }

  const recommendations = generateRecommendations(data.checks);

  return {
    url: data.url,
    overallScore: data.overallScore,
    checks: data.checks,
    recommendations,
    auditedAt: data.auditedAt,
    metrics: data.metrics,
  };
}